import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowRight, Printer, Copy, Eye, PlusCircle } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const styles = {
    complete: 'bg-blue-50 text-blue-700 border-blue-200', in_progress: 'bg-amber-50 text-amber-700 border-amber-200', abandoned: 'bg-zinc-100 text-zinc-400 border-zinc-200',
  };
  return <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${styles[status] || ''}`}>{(status || '').replace('_', ' ')}</Badge>;
};

export default function MyChecklistsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/runs').then(r => { setRuns(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const inProgress = runs.filter(r => r.status === 'in_progress');
  const completed = runs.filter(r => r.status === 'complete');

  const handleDuplicate = async (runId) => {
    try {
      const res = await api.post(`/runs/${runId}/duplicate`);
      toast.success('Run duplicated');
      window.location.href = `/checklists/${res.data.run.id}/question`;
    } catch (err) { toast.error(err.response?.data?.detail || 'Duplication failed'); }
  };

  if (loading) return <div className="p-6 text-sm text-zinc-400">Loading...</div>;

  return (
    <div className="p-6" data-testid="my-checklists-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-zinc-500">Your checklist runs</p>
        <Button size="sm" asChild className="rounded-sm text-xs gap-1.5" style={{ background: 'var(--brand-primary, #1C3557)' }}>
          <Link to="/checklists/new" data-testid="start-new-run-btn"><PlusCircle size={13} /> Start new run</Link>
        </Button>
      </div>

      {inProgress.length > 0 && (
        <div className="mb-6">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3">In progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {inProgress.map(run => (
              <Card key={run.id} className="rounded-sm border-zinc-200 shadow-none hover:border-zinc-300 transition-colors" data-testid={`run-card-${run.id}`}>
                <CardContent className="p-4">
                  <div className="font-semibold text-zinc-800 mb-0.5">{run.project_fields?.project_name || 'Untitled'}</div>
                  <div className="text-xs text-zinc-400 mb-0.5">{run.project_fields?.client_name || '-'}</div>
                  <div className="text-xs text-zinc-400 mb-3">Q{run.answers?.length || 0} answered</div>
                  <Button size="sm" asChild className="rounded-sm text-xs w-full gap-1" style={{ background: 'var(--brand-primary, #1C3557)' }}>
                    <Link to={`/checklists/${run.id}/question`}>Resume <ArrowRight size={12} /></Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3">Completed</h2>
        {completed.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm px-4 py-10 text-center text-sm text-zinc-400">No completed checklists yet</div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Project</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Client</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Completed</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completed.map(run => (
                  <TableRow key={run.id}>
                    <TableCell className="px-4 py-2.5 font-medium text-zinc-800">{run.project_fields?.project_name || '-'}</TableCell>
                    <TableCell className="px-4 py-2.5 text-zinc-500">{run.project_fields?.client_name || '-'}</TableCell>
                    <TableCell className="px-4 py-2.5 text-zinc-500 text-xs">{run.completed_at ? new Date(run.completed_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="px-4 py-2.5">
                      <div className="flex gap-1">
                        <Link to={`/checklists/${run.id}/complete`} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Eye size={14} /></Link>
                        <Link to={`/checklists/${run.id}/print`} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Printer size={14} /></Link>
                        <button onClick={() => handleDuplicate(run.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Copy size={14} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
